import React, { useState, useEffect, useCallback } from 'react'
import {
    Modal,
    Button,
    Spin,
    message,
    Empty,
    Pagination,
    Tabs,
    Input,
    Breadcrumb
} from 'antd'
import {
    FilePdfOutlined,
    FileWordOutlined,
    FilePptOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    FolderOutlined,
    HomeOutlined
} from '@ant-design/icons'
import _ from 'lodash'
import { api } from '../../services'
import './CourseDocumentSelectorModal.scss'

const { TabPane } = Tabs
const { Search } = Input

const CourseDocumentSelectorModal = ({ t, visible, onClose, onSelect }) => {
    const [documents, setDocuments] = useState([])
    const [folders, setFolders] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedDocs, setSelectedDocs] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [folderPage, setFolderPage] = useState(1)
    const [pageSize] = useState(10) // 10 items per page for both folders and files
    const [total, setTotal] = useState(0)
    const [folderTotal, setFolderTotal] = useState(0)
    const [activeTab, setActiveTab] = useState('myLibrary')
    const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('')
    const [inputValue, setInputValue] = useState('')
    const [parentId, setParentId] = useState(null)
    const [breadcrumbPath, setBreadcrumbPath] = useState([])

    // Accepted file formats
    const ACCEPTED_FORMATS = ['docx', 'doc', 'pdf', 'pptx', 'ppt', 'txt']

    useEffect(() => {
        if (visible) {
            fetchDocuments()
            fetchFolders()
        } else {
            // Reset when modal closes
            setCurrentPage(1)
            setFolderPage(1)
            setSelectedDocs([])
            setSearchQuery('')
            setInputValue('')
            setParentId(null)
            setBreadcrumbPath([])
        }
    }, [visible, activeTab, currentPage, folderPage, searchQuery, parentId])

    const getFileExtension = (filename) => {
        if (!filename) return 'file'
        const parts = filename.split('.')
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'file'
    }

    const formatFileType = (mimeType, filename) => {
        if (!mimeType) return getFileExtension(filename)

        if (mimeType.includes('pdf')) return 'pdf'
        if (mimeType.includes('word') || mimeType.includes('msword')) return 'docx'
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation'))
            return 'pptx'
        if (mimeType.includes('text/plain')) return 'txt'

        return getFileExtension(filename)
    }

    const isAcceptedFormat = (fileType) => {
        return ACCEPTED_FORMATS.includes(fileType.toLowerCase())
    }

    const trimFileName = (filename) => {
        if (!filename) return ''

        const maxLength = 25
        const parts = filename.split('.')
        const extension = parts.length > 1 ? parts.pop() : ''
        const nameWithoutExt = parts.join('.')

        if (filename.length <= maxLength) {
            return filename
        }

        const trimmedName = nameWithoutExt.substring(
            0,
            maxLength - extension.length - 3
        )
        return `${trimmedName}...${extension}`
    }

    const fetchDocuments = async () => {
        setLoading(true)
        try {
            const offset = (currentPage - 1) * pageSize

            const response = await api.library.getLibraryList({
                limit: pageSize,
                onlyMedia: true,
                offset,
                search: searchQuery,
                sort: 'NEWEST',
                type: 'PDF,DOC,PPT,TEXT',
                parent_id: parentId,
                is_ai_enabled: true,
                ...(activeTab === 'orgLibrary' && { isOrg: true }),
                ...(activeTab === 'myLibrary' && { isActiveOrg: true })
            })

            const transformedDocs = response.data.rows
                .filter((item) => item.is_ai_enabled === true)
                .map((item) => ({
                    id: item.id,
                    name: item.filename || item.title || item.name,
                    type: formatFileType(item.media_mime_type, item.filename),
                    category: activeTab,
                    owner: item.creator ? item.creator.name : 'Unknown',
                    lastUpdated: item.updated_at,
                    path: item.path,
                    link: item.link,
                    mimeType: item.media_mime_type
                }))
                .filter((doc) => isAcceptedFormat(doc.type))

            setDocuments(transformedDocs)
            setTotal(response.data.count || 0) // Use the total count from API
        } catch (error) {
            console.error('Error fetching documents:', error)
            message.error(t('v5:failed_to_load_documents') || 'Failed to load documents')
            setDocuments([])
            setTotal(0)
        } finally {
            setLoading(false)
        }
    }

    const fetchFolders = async () => {
        try {
            const offset = (folderPage - 1) * pageSize

            const response = await api.library.getSopList({
                limit: pageSize,
                offset,
                onlyFolder: true,
                parent_id: parentId,
                search: searchQuery,
                sort: 'NEWEST',
                ...(activeTab === 'orgLibrary' && { isOrg: true }),
                ...(activeTab === 'myLibrary' && { isActiveOrg: true })
            })

            const transformedFolders = response.data.rows.map((item) => ({
                id: item.id,
                name: item.name || item.title,
                type: 'folder',
                parent_id: item.parent_id
            }))

            setFolders(transformedFolders)
            setFolderTotal(response.data.count || 0)
        } catch (error) {
            console.error('Error fetching folders:', error)
            setFolders([])
            setFolderTotal(0)
        }
    }

    const getFileIcon = (type) => {
        const iconStyle = { fontSize: '36px' }
        switch (type.toLowerCase()) {
            case 'pdf':
                return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />
            case 'doc':
            case 'docx':
                return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />
            case 'ppt':
            case 'pptx':
                return <FilePptOutlined style={{ ...iconStyle, color: '#ff7a45' }} />
            case 'txt':
                return <FileTextOutlined style={{ ...iconStyle, color: '#52c41a' }} />
            case 'folder':
                return <FolderOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
            default:
                return <FileTextOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
        }
    }

    const handleDocumentClick = (doc) => {
        if (doc.type === 'folder') {
            // Navigate into folder
            setParentId(doc.id)
            setBreadcrumbPath([...breadcrumbPath, { id: doc.id, name: doc.name }])
            setCurrentPage(1)
            setFolderPage(1)
            // Clear data immediately to prevent flash of old content
            setDocuments([])
            setFolders([])
            setTotal(0)
            setFolderTotal(0)
        } else {
            // Toggle selection
            const isSelected = selectedDocs.some((d) => d.id === doc.id)

            if (isSelected) {
                setSelectedDocs(selectedDocs.filter((d) => d.id !== doc.id))
            } else {
                setSelectedDocs([...selectedDocs, doc])
            }
        }
    }

    const handleBreadcrumbClick = (index) => {
        if (index === -1) {
            // Go to root
            setParentId(null)
            setBreadcrumbPath([])
        } else {
            // Go to specific folder
            const folder = breadcrumbPath[index]
            setParentId(folder.id)
            setBreadcrumbPath(breadcrumbPath.slice(0, index + 1))
        }
        setCurrentPage(1)
        setFolderPage(1)
        // Clear data immediately to prevent flash of old content
        setDocuments([])
        setFolders([])
        setTotal(0)
        setFolderTotal(0)
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    const handleFolderPageChange = (page) => {
        setFolderPage(page)
    }

    const handleTabChange = (key) => {
        setActiveTab(key)
        setCurrentPage(1)
        setFolderPage(1)
        setSearchQuery('')
        setInputValue('')
        setParentId(null)
        setBreadcrumbPath([])
        // Clear data immediately to prevent flash of old content
        setDocuments([])
        setFolders([])
        setTotal(0)
        setFolderTotal(0)
    }

    const debouncedSearch = useCallback(
        _.debounce((value) => {
            setSearchQuery(value)
            setCurrentPage(1)
        }, 500),
        []
    )

    const handleSearchChange = (e) => {
        const value = e.target.value
        setInputValue(value)
        debouncedSearch(value)
    }

    const handleManualSearch = (value) => {
        debouncedSearch.cancel()
        setInputValue(value)
        setSearchQuery(value)
        setCurrentPage(1)
    }

    const handleSelect = () => {
        if (selectedDocs.length > 0) {
            onSelect(selectedDocs)
            handleClose()
        } else {
            message.warning(t('v5:please_select_at_least_one_document') || 'Please select at least one document')
        }
    }

    const handleClose = () => {
        setSelectedDocs([])
        setCurrentPage(1)
        setSearchQuery('')
        setInputValue('')
        setParentId(null)
        setBreadcrumbPath([])
        onClose()
    }

    const isDocSelected = (docId) => {
        return selectedDocs.some((d) => d.id === docId)
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        const now = new Date()
        const diffTime = Math.abs(now - date)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return t('v5:today') || 'Today'
        if (diffDays === 1) return t('v5:yesterday') || 'Yesterday'
        if (diffDays < 7) return `${diffDays} ${t('v5:days_ago') || 'days ago'}`
        if (diffDays < 30)
            return `${Math.floor(diffDays / 7)} ${t('v5:weeks_ago') || 'weeks ago'}`
        if (diffDays < 365)
            return `${Math.floor(diffDays / 30)} ${t('v5:months_ago') || 'months ago'}`
        return date.toLocaleDateString()
    }

    const renderGridView = () => (
        <>
            {/* Folders Section */}
            {folderTotal > 0 && (
                <div className="section-container">
                    <div className="section-header">
                        <h3>{t('v5:folders') || 'Folders'} ({folderTotal})</h3>
                    </div>
                    <div className="documents-grid">
                        {folders.map((item) => (
                            <div
                                key={item.id}
                                className="document-tile folder-tile"
                                onClick={() => handleDocumentClick(item)}
                            >
                                <div className="document-icon">{getFileIcon(item.type)}</div>
                                <div className="document-name" title={item.name}>
                                    {trimFileName(item.name)}
                                </div>
                            </div>
                        ))}
                    </div>
                    {folderTotal > pageSize && (
                        <div className="pagination-container">
                            <Pagination
                                current={folderPage}
                                pageSize={pageSize}
                                total={folderTotal}
                                onChange={handleFolderPageChange}
                                showSizeChanger={false}
                                size="small"
                                showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} ${t('v5:of') || 'of'} ${total} ${t('v5:folders') || 'folders'
                                    }`
                                }
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Files Section */}
            {total > 0 && (
                <div className="section-container">
                    <div className="section-header">
                        <h3>{t('v4:files') || 'Files'} ({total})</h3>
                    </div>
                    <div className="documents-grid">
                        {documents.map((item) => (
                            <div
                                key={item.id}
                                className={`document-tile ${isDocSelected(item.id) ? 'selected' : ''
                                    }`}
                                onClick={() => handleDocumentClick(item)}
                            >
                                {isDocSelected(item.id) && (
                                    <div className="selected-badge">
                                        <CheckCircleOutlined />
                                    </div>
                                )}
                                <div className="document-icon">{getFileIcon(item.type)}</div>
                                <div className="document-name" title={item.name}>
                                    {trimFileName(item.name)}
                                </div>
                                <div className="document-meta">
                                    <div className="document-owner" title={item.owner}>
                                        {item.owner}
                                    </div>
                                    <div className="document-date">
                                        {formatDate(item.lastUpdated)}
                                    </div>
                                </div>
                                <div className="document-type">{item.type.toUpperCase()}</div>
                            </div>
                        ))}
                    </div>
                    {total > pageSize && (
                        <div className="pagination-container">
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={total}
                                onChange={handlePageChange}
                                showSizeChanger={false}
                                size="small"
                                showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} ${t('v5:of') || 'of'} ${total} ${t('v5:documents') || 'documents'
                                    }`
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    )

    const renderListView = () => (
        <>
            {/* Folders Section */}
            {folderTotal > 0 && (
                <div className="section-container">
                    <div className="section-header">
                        <h3>{t('v4:folders') || 'Folders'} ({folderTotal})</h3>
                    </div>
                    <div className="documents-list">
                        {folders.map((item) => (
                            <div
                                key={item.id}
                                className="document-list-item folder-item"
                                onClick={() => handleDocumentClick(item)}
                            >
                                <div className="list-item-icon">{getFileIcon(item.type)}</div>
                                <div className="list-item-details">
                                    <div className="list-item-name" title={item.name}>
                                        {item.name}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {folderTotal > pageSize && (
                        <div className="pagination-container">
                            <Pagination
                                current={folderPage}
                                pageSize={pageSize}
                                total={folderTotal}
                                onChange={handleFolderPageChange}
                                showSizeChanger={false}
                                size="small"
                                showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} ${t('v5:of') || 'of'} ${total} ${t('v4:folders') || 'folders'
                                    }`
                                }
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Files Section */}
            {total > 0 && (
                <div className="section-container">
                    <div className="section-header">
                        <h3>{t('v4:files') || 'Files'} ({total})</h3>
                    </div>
                    <div className="documents-list">
                        {documents.map((item) => (
                            <div
                                key={item.id}
                                className={`document-list-item ${isDocSelected(item.id) ? 'selected' : ''
                                    }`}
                                onClick={() => handleDocumentClick(item)}
                            >
                                <div className="list-item-icon">{getFileIcon(item.type)}</div>
                                <div className="list-item-details">
                                    <div className="list-item-name" title={item.name}>
                                        {item.name}
                                    </div>
                                    <div className="list-item-meta">
                                        <span className="list-item-owner">{item.owner}</span>
                                        <span className="list-item-separator">•</span>
                                        <span className="list-item-date">
                                            {formatDate(item.lastUpdated)}
                                        </span>
                                        <span className="list-item-separator">•</span>
                                        <span className="list-item-type">
                                            {item.type.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                {isDocSelected(item.id) && (
                                    <CheckCircleOutlined
                                        className="list-item-check"
                                        style={{ fontSize: '20px', color: '#1890ff' }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    {total > pageSize && (
                        <div className="pagination-container">
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={total}
                                onChange={handlePageChange}
                                showSizeChanger={false}
                                size="small"
                                showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} ${t('v5:of') || 'of'} ${total} ${t('v4:documents') || 'documents'
                                    }`
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    )

    return (
        <Modal
            visible={visible}
            title={
                <div>
                    {t('v5:select_documents') || 'Select Document(s)'}
                    {selectedDocs.length > 0 && (
                        <span style={{ marginLeft: '10px', color: '#1890ff', fontSize: '14px' }}>
                            ({selectedDocs.length} {t('v5:selected') || 'selected'})
                        </span>
                    )}
                </div>
            }
            onCancel={handleClose}
            width={1000}
            footer={[
                <Button key="close" onClick={handleClose}>
                    {t('buttons:close') || 'Close'}
                </Button>,
                <Button
                    key="select"
                    type="primary"
                    onClick={handleSelect}
                    disabled={selectedDocs.length === 0}
                >
                    {t('v2:button_submit') || 'Select'} ({selectedDocs.length})
                </Button>
            ]}
            className="course-document-selector-modal"
            centered
        >
            <div className="document-selector-content">
                <div className="controls-bar">
                    <Search
                        placeholder={t('v5:search_documents_by_name') || 'Search documents...'}
                        allowClear
                        value={inputValue}
                        onChange={handleSearchChange}
                        onSearch={handleManualSearch}
                        style={{ width: 300, marginRight: 'auto' }}
                    />
                    <div className="view-toggle">
                        <Button
                            type={viewMode === 'grid' ? 'primary' : 'default'}
                            icon="appstore"
                            onClick={() => setViewMode('grid')}
                            title={t('v5:grid_view') || 'Grid View'}
                        />
                        <Button
                            type={viewMode === 'list' ? 'primary' : 'default'}
                            icon="unordered-list"
                            onClick={() => setViewMode('list')}
                            style={{ marginLeft: '8px' }}
                            title={t('v5:list_view') || 'List View'}
                        />
                    </div>
                </div>

                {/* Breadcrumb Navigation */}
                {breadcrumbPath.length > 0 && (
                    <div className="breadcrumb-container">
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <a onClick={() => handleBreadcrumbClick(-1)}>
                                    <HomeOutlined /> {t('v4:home') || 'Home'}
                                </a>
                            </Breadcrumb.Item>
                            {breadcrumbPath.map((folder, index) => (
                                <Breadcrumb.Item key={folder.id}>
                                    <a onClick={() => handleBreadcrumbClick(index)}>{folder.name}</a>
                                </Breadcrumb.Item>
                            ))}
                        </Breadcrumb>
                    </div>
                )}

                <Tabs activeKey={activeTab} onChange={handleTabChange}>
                    <TabPane tab={t('v5:my_library') || 'My Library'} key="myLibrary">
                        {loading ? (
                            <div className="loading-container">
                                <Spin size="large" tip={t('v5:loading_documents') || 'Loading documents...'} />
                            </div>
                        ) : folderTotal === 0 && total === 0 ? (
                            <Empty description={t('v5:no_documents_available') || 'No documents available'} />
                        ) : (
                            <>
                                {viewMode === 'grid' ? renderGridView() : renderListView()}
                            </>
                        )}
                    </TabPane>

                    <TabPane
                        tab={t('media:organization_library') || 'Organization Library'}
                        key="orgLibrary"
                    >
                        {loading ? (
                            <div className="loading-container">
                                <Spin size="large" tip={t('v5:loading_documents') || 'Loading documents...'} />
                            </div>
                        ) : folderTotal === 0 && total === 0 ? (
                            <Empty description={t('v5:no_documents_available') || 'No documents available'} />
                        ) : (
                            <>
                                {viewMode === 'grid' ? renderGridView() : renderListView()}
                            </>
                        )}
                    </TabPane>
                </Tabs>
            </div>
        </Modal>
    )
}

export default CourseDocumentSelectorModal
